import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, Share2, Calendar, Megaphone, Mail,
  MessageSquare, Globe, Image as ImageIcon, Film, Bookmark, ChevronDown, ChevronRight, Layers,
  Building2, Users, Contact2, Wand2,
} from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "@/contexts/AuthContext";

// Each item declares which roles can see it
const SUPER = "super_admin";
const BADMIN = "brand_admin";
const MARKETER = "marketer";
const CRMUSER = "crm_user";
const VIEWER = "viewer";

const ALL_ITEMS = [
  { to: "/optimus", label: "Optimus Prime", icon: Sparkles, roles: [SUPER, BADMIN] },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: [SUPER, BADMIN, MARKETER] },
  { to: "/brands", label: "Brands", icon: Building2, roles: [SUPER, BADMIN] },
  { to: "/approval-queue", label: "Approval Queue", icon: Sparkles, roles: [SUPER, BADMIN, MARKETER] },
  { to: "/quick-post", label: "Quick Post", icon: Wand2, roles: [SUPER, BADMIN, MARKETER] },
  { to: "/bulk-post", label: "Bulk Post", icon: Layers, roles: [SUPER, BADMIN, MARKETER] },
  { to: "/users", label: "Users", icon: Users, roles: [SUPER] },
  { to: "/crm", label: "CRM", icon: Contact2, roles: [SUPER, BADMIN, CRMUSER, VIEWER] },
  { to: "/campaign-bundle", label: "Campaign Bundle", icon: Layers, roles: [SUPER, BADMIN, MARKETER] },
  { to: "/swipe-file", label: "Swipe File", icon: Bookmark, roles: [SUPER, BADMIN, MARKETER] },
  {
    label: "Social Media", icon: Share2, key: "social",
    roles: [SUPER, BADMIN, MARKETER],
    children: [
      { to: "/social/recommendations", label: "Recommendations" },
      { to: "/social/my-videos", label: "My Videos" },
      { to: "/social/content-studio", label: "AI Content Studio" },
    ],
  },
  { to: "/calendar", label: "Content Calendar", icon: Calendar, roles: [SUPER, BADMIN, MARKETER, VIEWER] },
  { to: "/paid-ads", label: "Paid Ads", icon: Megaphone, roles: [SUPER, BADMIN, MARKETER] },
  { to: "/email", label: "Email Marketing", icon: Mail, roles: [SUPER, BADMIN, MARKETER] },
  { to: "/sms", label: "SMS Marketing", icon: MessageSquare, roles: [SUPER, BADMIN, MARKETER] },
  {
    label: "Website", icon: Globe, key: "website",
    roles: [SUPER, BADMIN, MARKETER],
    children: [{ to: "/website/seo", label: "SEO & AI Visibility" }],
  },
  { to: "/media", label: "Media Library", icon: ImageIcon, roles: [SUPER, BADMIN, MARKETER] },
  {
    label: "AI Video Factory", icon: Film, key: "vf",
    roles: [SUPER, BADMIN, MARKETER],
    children: [
      { to: "/video-factory", label: "Dashboard" },
      { to: "/video-factory/new", label: "New Video" },
      { to: "/video-factory/batch", label: "Batch Mode" },
      { to: "/video-factory/history", label: "History" },
    ],
  },
];

const Sidebar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState({ social: true, website: false, vf: true });

  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  const role = user?.role || SUPER;
  const sidebarItems = ALL_ITEMS.filter((it) => it.roles.includes(role));

  return (
    <aside className="w-[200px] min-h-screen bg-white border-r border-gray-200 flex flex-col" data-testid="sidebar">
      <div className="px-4 py-5 border-b border-gray-100">
        <Logo size={26} />
      </div>
      <nav className="flex-1 py-3 overflow-y-auto">
        {sidebarItems.map((item, idx) => {
          const Icon = item.icon;
          if (item.children) {
            const isOpen = expanded[item.key];
            const hasActive = item.children.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to));
            return (
              <div key={idx}>
                <button
                  onClick={() => toggle(item.key)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium ${hasActive ? 'text-[#7C3AED]' : 'text-gray-700'} hover:bg-gray-50`}
                  data-testid={`sidebar-toggle-${item.key}`}
                >
                  <span className="flex items-center gap-3">
                    <Icon size={18} />
                    {item.label}
                  </span>
                  {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {isOpen && (
                  <div className="ml-2">
                    {item.children.map((c) => (
                      <NavLink
                        key={`${item.label}-${c.to}`}
                        to={c.to}
                        className={({ isActive }) =>
                          `block pl-12 pr-4 py-2 text-sm relative ${isActive ? 'bg-[#EDE9FE] text-[#7C3AED] font-medium' : 'text-gray-600 hover:bg-gray-50'}`
                        }
                        data-testid={`sidebar-link-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#7C3AED] rounded-r" />}
                            {c.label}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <NavLink
              key={`${idx}-${item.to}`}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm font-medium relative ${isActive ? 'bg-[#EDE9FE] text-[#7C3AED]' : 'text-gray-700 hover:bg-gray-50'}`
              }
              data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#7C3AED] rounded-r" />}
                  <Icon size={18} />
                  {item.label}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
};

export default Sidebar;
